<template>
  <section class="promotion-banner">
    <div class="promotion-banner__container">
      <div class="promotion-banner__picture">
        <img
          :src="getLocalizedName(category.promotion.coverUrl)"
          alt="banner"
        />
      </div>
      <div class="promotion-banner__content">
        <span v-if="!mobileSize" class="promotion-banner__round-top"></span>
        <span v-if="!mobileSize" class="promotion-banner__round-bottom"></span>
        <div class="promotion-banner__box">
          <h1 class="promotion-banner__title">
            {{ category.getName() }}
          </h1>
          <p
            class="promotion-banner__description"
            v-html="getLocalizedName(category.promotion.description)"
          />
          <NuxtLink
            v-if="!mobileSize && !timeOff"
            href="#description"
            class="promotion-banner__button"
          >
            <span>{{ _T("@More details") }}</span>
            <div class="promotion-banner__button-icon">
              <img src="~/assets/icons/promotion-arrow.svg" alt="arrow" />
            </div>
          </NuxtLink>
        </div>
        <PromotionTimer
          :period="category.promotion.period"
          :time-off="timeOff"
        />

        <div v-if="mobileSize" class="promotion-banner__dashed-line">
          <span class="promotion-banner__round-top"></span>
          <span class="promotion-banner__round-bottom"></span>
        </div>

        <NuxtLink
          v-if="mobileSize && !timeOff"
          href="#description"
          class="promotion-banner__button"
        >
          <span>{{ _T("@More details") }}</span>
          <div class="promotion-banner__button-icon">
            <img src="~/assets/icons/promotion-arrow.svg" alt="arrow" />
          </div>
        </NuxtLink>

        <div v-if="mobileSize && timeOff" class="promotion-banner__footer">
          <MyLink
            name="category"
            :params="{
              categorySlug: getCategoryByCode(promotionCategoryCode).slug,
            }"
            class="promotion-banner__footer-link"
          >
            {{ _T(timeOffText.allPromotions) }}
          </MyLink>
          {{ _T("@or") }}
          <MyLink name="home" class="promotion-banner__footer-link">
            {{ _T(timeOffText.home) }}
          </MyLink>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import PromotionTimer from "~/modules/productCatalog/ui/pages/promotions/PromotionTimer.vue";
import { addScopedEventListener } from "~/utils/eventListener";
import { useCategoriesStore } from "~/store/categoriesStore";
import { promotionCategoryCode } from "~/config/category.config";
import { Category } from "~/models/category.model";

const props = defineProps({
  category: { type: Category, required: true },
});

const categoryStore = useCategoriesStore();
const { getCategoryByCode } = storeToRefs(categoryStore);

const timeOff = ref();
const mobileSize = ref();

const timeOffText = ref({
  allPromotions: "@View all promotions",
  home: "@Go back to main page",
});

function getInitData() {
  timeOff.value = props.category.promotion.period.endAt * 1000 <= new Date();
}

function isMobile() {
  mobileSize.value = window.innerWidth <= 1024;
}

onMounted(() => {
  isMobile();
  getInitData();
  addScopedEventListener(window, "resize", isMobile);
});
</script>

<style lang="scss" scoped>
.promotion-banner {
  padding: 0 16px 32px;

  &__container {
    max-height: 480px;
    @extend %width-main;
    margin: auto;

    @include flex-container(row, space-between);

    @include bigMobile {
      max-height: max-content;
      @include flex-container(column, center, center);
      gap: 16px;
    }
  }

  &__picture {
    position: relative;
    height: inherit;
    overflow: hidden;
    border-radius: 16px;

    @include bigMobile {
      max-height: 480px;
      height: 100%;
    }
    & img {
      width: 100%;
      display: flex;
      object-fit: cover;
      height: 100%;
    }
  }

  &__round-top,
  &__round-bottom {
    position: absolute;
    z-index: 10;
    left: -25px;

    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: var(--color-sky-lightest);

    @include smallestScreen {
      width: 40px;
      height: 40px;
      left: -20px;
    }

    @include bigMobile {
      width: 28px;
      height: 28px;
      left: auto;
    }
  }

  &__round-top {
    top: -25px;

    @include bigMobile {
      top: -13px;
      left: -29px;
    }
  }
  &__round-bottom {
    bottom: -25px;

    @include bigMobile {
      bottom: auto;
      top: -13px;
      right: -29px;
    }
  }

  &__deadline-box {
    position: absolute;
    z-index: 10;
    top: 50%;
    right: 10%;
    transform: translateY(-50%);

    max-width: 326px;
    width: 100%;
    color: black;

    @include bigMobile {
      max-width: 250px;
    }

    @include mobile {
      max-width: 136px;
      right: 27px;
    }
  }

  &__deadline-title {
    @include font(32, 42, 600);
    margin-bottom: 24px;

    @include bigMobile {
      @include font(24, 30, 600);
      margin-bottom: 8px;
    }

    @include mobile {
      @include font(12, 16, 600);
      margin-bottom: 8px;
    }
  }

  &__deadline-description {
    @include font(16, 22, 400);
    letter-spacing: 0.32px;
    margin-bottom: 16px;

    @include bigMobile {
      @include font(12, 16, 400);
      margin-bottom: 0;
    }

    @include mobile {
      @include font(8, 11, 400);
    }
  }

  &__dashed-line {
    position: relative;
    width: 100%;
    border-bottom: 2px dashed var(--color-primary-base);
  }

  &__content {
    position: relative;

    max-width: 480px;

    background-color: white;

    padding: 16px 54px;
    border-radius: 16px;
    border-left: 2px dashed var(--color-primary-base);

    @include flex-container(column, center);
    gap: 48px;

    @include smallestScreen {
      max-width: 400px;
      padding: 16px 20px;
      gap: 24px;
    }

    @include bigMobile {
      max-width: 100%;
      width: 100%;
      border: none;
      padding: 25px 15px;

      align-items: center;
      text-align: center;
    }

    &.deadline {
      @include bigMobile {
        padding: 55px 15px 25px;
      }
    }
  }

  &__box {
    @include flex-container(column);
    gap: 16px;

    &.deadline {
      position: relative;
      @include flex-container(row, center, center);
      gap: 8px;

      @include mobile {
        align-items: flex-end;
      }

      & span {
        @include font(16, 22, 700);

        @include mobile {
          position: absolute;
          z-index: 10;
          right: -25px;
          bottom: 5px;
          @include font(16, 22, 700);
        }
      }
    }
  }

  &__title {
    @include font(25, 35, 700);
    letter-spacing: 0.5px;
    text-transform: uppercase;

    @include smallestScreen {
      @include font(20, 30, 700);
    }

    @include mobile {
      text-transform: none;
      @include font(16, 23, 700);
      letter-spacing: 0.32px;
    }

    &.deadline {
      @include mobile {
        max-width: 170px;
        @include font(24, 34, 700);
        letter-spacing: 0.48px;
      }
    }
  }

  &__description {
    @include font(16, 20, 400);

    @include smallestScreen {
      @include font(14, 18, 500);
    }
  }
  &__button {
    position: relative;
    width: max-content;
    padding: 8px 48px 8px 16px;
    border-radius: 8px;

    color: var(--color-primary-base);
    background-color: rgba(0, 155, 62, 0.2);
    @include font(16, 24, 700);

    cursor: pointer;

    &:hover img {
      animation: arrow-move-forward 0.4s ease-in forwards;
    }
  }

  &__button-icon {
    position: absolute;
    z-index: 10;
    right: 0;
    top: 0;

    overflow: hidden;
    height: max-content;

    padding: 8px 16px 8px 0;
    border-radius: 8px;
  }

  &__footer {
    @include font(16, 22, 500);
    letter-spacing: 0.32px;

    @include mobile {
      text-align: left;
      max-width: 280px;
    }
  }

  &__footer-link {
    color: var(--color-primary-base);
    border-bottom: 1px dashed var(--color-primary-base);
  }
}
</style>
